import { getDb } from '@/services/db/database';
import { migrate } from '@/services/db/migrations';
import { CURRENT_SCHEMA_VERSION, type CvData } from '@/types/cv';
import { generateId } from '@/utils/id';
import { nowIso } from '@/utils/date';

export function createEmptyCv(name = 'Moje CV'): CvData {
  const now = nowIso();
  return {
    id: generateId(),
    metadata: {
      createdAt: now,
      updatedAt: now,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      name,
    },
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      websites: [],
    },
    experience: [],
    education: [],
    skills: [],
    languages: [],
    projects: [],
    certifications: [],
    customSections: [],
    settings: {
      selectedTemplate: 'classic',
      language: 'pl',
      color: '#1f2937',
      sectionOrder: [
        { key: 'experience', visible: true, order: 0 },
        { key: 'education', visible: true, order: 1 },
        { key: 'skills', visible: true, order: 2 },
        { key: 'languages', visible: true, order: 3 },
        { key: 'projects', visible: true, order: 4 },
        { key: 'certifications', visible: true, order: 5 },
        { key: 'customSections', visible: true, order: 6 },
      ],
    },
  };
}

export const cvRepository = {
  async create(name?: string): Promise<CvData> {
    const cv = createEmptyCv(name);
    await getDb().cvs.put(cv);
    return cv;
  },

  async save(cv: CvData): Promise<void> {
    await getDb().cvs.put({ ...cv, metadata: { ...cv.metadata, updatedAt: nowIso() } });
  },

  async getById(id: string): Promise<CvData | undefined> {
    const cv = await getDb().cvs.get(id);
    if (!cv) return undefined;
    return migrate(cv);
  },

  async getAll(): Promise<CvData[]> {
    const items = await getDb().cvs.toArray();
    return items
      .map(migrate)
      .sort((a, b) => (b.metadata.updatedAt ?? '').localeCompare(a.metadata.updatedAt ?? ''));
  },

  async update(id: string, partial: Partial<CvData>): Promise<CvData | undefined> {
    const existing = await this.getById(id);
    if (!existing) return undefined;
    const merged: CvData = {
      ...existing,
      ...partial,
      id,
      metadata: {
        ...existing.metadata,
        ...(partial.metadata ?? {}),
        updatedAt: nowIso(),
        schemaVersion: CURRENT_SCHEMA_VERSION,
      },
    };
    await getDb().cvs.put(merged);
    return merged;
  },

  async delete(id: string): Promise<void> {
    await getDb().cvs.delete(id);
  },

  async getOrCreate(): Promise<CvData> {
    const all = await this.getAll();
    if (all.length > 0 && all[0]) return all[0];
    return this.create();
  },
};
